import { ComboBox, Dropdown, IconButton, IDropdownOption, ITooltipHostStyles, Label, Stack, TextField, TooltipHost } from '@fluentui/react';
import { ColumnDto, DataverseConfiguration, DataverseIntegration } from '../../../../../../../api/generated/data-contracts';
import { Dispatch, SetStateAction, useRef } from 'react';
import { DATAVERSE_PROJECT_COLUMNS, DESELECT_KEY } from '../../../../../../../constants/queryKeys';
import produce from 'immer';
import { useQuery } from '@tanstack/react-query';
import { ApiCalls } from '../../../../../../../api/api';
import { onRenderOption } from '../../../components/ComboBoxTooltip';
import { useId } from '@fluentui/react-hooks';
import { GetHostName } from '../../../../../../../helpers/trimUrl';

interface ITaskPaneProps {
    config: DataverseConfiguration;
    dataverseTables: IDropdownOption<ColumnDto>[];
    setIntegration: Dispatch<SetStateAction<DataverseIntegration>>;
}

export const TaskPane = (props: ITaskPaneProps) => {
    const { dataverseUrl, taskTableLogicalName } = props.config;

    const defaultColumns = useRef([] as IDropdownOption<ColumnDto>[]).current;
    const { data: entityOptions = defaultColumns } = useQuery({
        queryKey: [DATAVERSE_PROJECT_COLUMNS, dataverseUrl, taskTableLogicalName],
        queryFn: () =>
            ApiCalls.dataverseEnvironmentsTablesColumnsList({
                environmentUrl: GetHostName(dataverseUrl),
                tableName: taskTableLogicalName,
            }).then(
                res =>
                    [
                        { key: DESELECT_KEY, text: 'Not selected', data: { logicalName: '' } },
                        ...res.data.map(
                            column => ({ key: column.logicalName, text: column.displayName || column.logicalName, data: column } as IDropdownOption<ColumnDto>),
                        ),
                    ] as IDropdownOption<ColumnDto>[],
            ),
        enabled: Boolean(dataverseUrl && taskTableLogicalName),
        suspense: true,
    });

    const tooltipHostStyles: Partial<ITooltipHostStyles> = {
        root: { display: 'inline-block', marginLeft: 4 }, // Adjust the margin as needed
    };

    const calloutProps = { gapSpace: 0 };
    const autoCloseTasksTooltipId = useId('autoCloseTaskTypesTooltip');

    return (
        <Stack tokens={{ childrenGap: 5 }}>
            <ComboBox
                label="Task Table"
                allowFreeform
                autoComplete="on"
                required
                errorMessage={taskTableLogicalName ? '' : 'Table required'}
                options={props.dataverseTables}
                onChange={(e, opt, index, value) => {
                    if (opt) {
                        props.setIntegration(
                            produce(integration => {
                                integration.configuration.taskTableLogicalName = opt.key as string;
                                integration.configuration.taskTableLogicalCollectionName = opt.data.logicalCollectionName;
                            }),
                        );
                    } else if (!value) {
                        props.setIntegration(
                            produce(integration => {
                                integration.configuration.taskTableLogicalName = '';
                                integration.configuration.taskTableLogicalCollectionName = '';
                            }),
                        );
                    }
                }}
                selectedKey={taskTableLogicalName ?? ''}
                onRenderOption={onRenderOption}
            />
            <ComboBox
                label="Task Id"
                allowFreeform
                autoComplete="on"
                required
                errorMessage={props.config.taskIdColumn ? '' : 'Task Id column required'}
                options={entityOptions}
                onChange={(e, opt, index, value) => {
                    props.setIntegration(
                        produce(integration => {
                            integration.configuration.taskIdColumn = (opt?.key as string) || value || '';
                        }),
                    );
                }}
                disabled={entityOptions.length <= 0}
                selectedKey={props.config.taskIdColumn}
                onRenderOption={onRenderOption}
            />
            <ComboBox
                label="Task Name"
                allowFreeform
                autoComplete="on"
                required
                errorMessage={props.config.taskNameColumn ? '' : 'Task Name column required'}
                options={entityOptions}
                onChange={(e, opt, index, value) => {
                    props.setIntegration(
                        produce(integration => {
                            integration.configuration.taskNameColumn = (opt?.key as string) || value || '';
                        }),
                    );
                }}
                disabled={entityOptions.length <= 0}
                selectedKey={props.config.taskNameColumn ?? ''}
                onRenderOption={onRenderOption}
            />
            <ComboBox
                label="Project Relation"
                allowFreeform
                autoComplete="on"
                required
                errorMessage={props.config.taskProjectIdColumn ? '' : 'Project Id column required'}
                options={entityOptions}
                onChange={(e, opt, index, value) => {
                    props.setIntegration(
                        produce(integration => {
                            integration.configuration.taskProjectIdColumn = (opt?.key as string) || value || '';
                        }),
                    );
                }}
                disabled={entityOptions.length <= 0}
                selectedKey={props.config.taskProjectIdColumn ?? ''}
                onRenderOption={onRenderOption}
            />
            <Dropdown
                onRenderLabel={() => (
                    <Stack horizontal verticalAlign="center">
                        <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 0 }}>
                            <Label>Auto Close Task Types</Label>
                            <TooltipHost
                                id={autoCloseTasksTooltipId}
                                calloutProps={calloutProps}
                                styles={tooltipHostStyles}
                                content={'This overrules the task closed column configuration. Can be useful if you only want to work with either task type.'}
                            >
                                <IconButton iconProps={{ iconName: 'Info' }} aria-describedby={autoCloseTasksTooltipId} ariaLabel="Info" title="Info" />
                            </TooltipHost>
                        </Stack>
                    </Stack>
                )}
                label="Auto Close Task Types"
                multiSelect={true}
                options={taskTypeOptions}
                selectedKeys={props.config.autoCloseTaskTypes}
                onChange={(event: React.FormEvent<HTMLDivElement>, item: IDropdownOption) => {
                    props.setIntegration(
                        produce(integration => {
                            integration.configuration.autoCloseTaskTypes = item.selected
                                ? [...(integration.configuration.autoCloseTaskTypes || []), item.key as string]
                                : (integration.configuration.autoCloseTaskTypes || []).filter(x => x !== item.key);
                        }),
                    );
                }}
                disabled={entityOptions.length <= 0}
            />
            <ComboBox
                label="Task Number"
                allowFreeform
                autoComplete="on"
                options={entityOptions}
                onChange={(e, opt, index, value) => {
                    props.setIntegration(
                        produce(integration => {
                            integration.configuration.taskNumberColumn = (opt?.key as string) || value || '';
                        }),
                    );
                }}
                disabled={entityOptions.length <= 0}
                selectedKey={props.config.taskNumberColumn ?? ''}
                onRenderOption={onRenderOption}
            />
            <ComboBox
                label="Task Start"
                allowFreeform
                autoComplete="on"
                options={entityOptions}
                onChange={(e, opt, index, value) => {
                    props.setIntegration(
                        produce(integration => {
                            integration.configuration.taskStartColumn = (opt?.key as string) || value || '';
                        }),
                    );
                }}
                disabled={entityOptions.length <= 0}
                selectedKey={props.config.taskStartColumn ?? ''}
                onRenderOption={onRenderOption}
            />
            {/* Add project number */}
            <ComboBox
                label="Task End"
                allowFreeform
                autoComplete="on"
                options={entityOptions}
                onChange={(e, opt, index, value) => {
                    props.setIntegration(
                        produce(integration => {
                            integration.configuration.taskEndColumn = (opt?.key as string) || value || '';
                        }),
                    );
                }}
                disabled={entityOptions.length <= 0}
                selectedKey={props.config.taskEndColumn ?? ''}
                onRenderOption={onRenderOption}
            />
            <ComboBox
                label="Order"
                allowFreeform
                autoComplete="on"
                options={entityOptions}
                onChange={(e, opt, index, value) => {
                    props.setIntegration(
                        produce(integration => {
                            integration.configuration.taskOrderColumn = (opt?.key as string) || value || '';
                        }),
                    );
                }}
                disabled={entityOptions.length <= 0}
                selectedKey={props.config.taskOrderColumn ?? ''}
                onRenderOption={onRenderOption}
            />
            <ComboBox
                label="Work Type Id"
                allowFreeform
                autoComplete="on"
                options={entityOptions}
                onChange={(e, opt, index, value) => {
                    props.setIntegration(
                        produce(integration => {
                            integration.configuration.taskWorkTypeIdColumn = (opt?.key as string) || value || '';
                        }),
                    );
                }}
                disabled={entityOptions.length <= 0}
                selectedKey={props.config.taskWorkTypeIdColumn ?? ''}
                onRenderOption={onRenderOption}
            />
            <ComboBox
                label="Task Closed"
                allowFreeform
                autoComplete="on"
                options={entityOptions}
                onChange={(e, opt, index, value) => {
                    props.setIntegration(
                        produce(integration => {
                            integration.configuration.taskClosedColumn = (opt?.key as string) || value || '';
                        }),
                    );
                }}
                disabled={entityOptions.length <= 0}
                selectedKey={props.config.taskClosedColumn ?? ''}
                onRenderOption={onRenderOption}
            />
            <TextField
                label={'Task Closed Compare Value'}
                onChange={(e, value) =>
                    props.setIntegration(
                        produce(integration => {
                            integration.configuration.taskClosedValue = value ?? '';
                        }),
                    )
                }
                value={props.config.taskClosedValue ?? ''}
                disabled={!props.config.taskClosedColumn}
            />
            {/* </Suspense> */}
        </Stack>
    );
};

const taskTypeOptions: IDropdownOption<string>[] = [
    { key: 'Project', text: 'Project summary' },
    { key: 'Milestone', text: 'Milestone' },
    { key: 'Task', text: 'Task' },
];
